import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SettingRow = ({ label, description, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4 border-b border-border last:border-0">
    <div className="flex-1">
      <p className="text-sm font-medium text-foreground">{label}</p>
      {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
    </div>
    <div className="sm:w-48 flex-shrink-0">{children}</div>
  </div>
);

const PlatformSettings = () => {
  const [settings, setSettings] = useState({
    engagementWeight: 40,
    reliabilityWeight: 35,
    reachWeight: 25,
    bronzeCashback: 5,
    silverCashback: 8,
    goldCashback: 12,
    platinumCashback: 15,
    maxRewardPerSubmission: 50,
    maxMonthlyReward: 500,
    maxCampaignBudget: 10000,
    minFollowers: 1000,
    minScore: 10,
    minApprovalRate: 60,
  });
  const [saved, setSaved] = useState(false);

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const sections = [
    {
      title: 'SocialCents Score Weights',
      icon: 'Star',
      description: 'Configure how each factor contributes to the overall SocialCents score. Total should equal 100%.',
      settings: [
        { key: 'engagementWeight', label: 'Engagement Weight (%)', description: 'Weight of engagement metrics in score calculation' },
        { key: 'reliabilityWeight', label: 'Reliability Weight (%)', description: 'Weight of submission reliability in score calculation' },
        { key: 'reachWeight', label: 'Reach Weight (%)', description: 'Weight of follower reach in score calculation' },
      ],
    },
    {
      title: 'Cashback Tier Rules',
      icon: 'Percent',
      description: 'Set cashback percentages for each influencer tier.',
      settings: [
        { key: 'bronzeCashback', label: 'Bronze Tier Cashback (%)', description: 'Cashback % for Bronze tier influencers (score 0-25)' },
        { key: 'silverCashback', label: 'Silver Tier Cashback (%)', description: 'Cashback % for Silver tier influencers (score 26-50)' },
        { key: 'goldCashback', label: 'Gold Tier Cashback (%)', description: 'Cashback % for Gold tier influencers (score 51-75)' },
        { key: 'platinumCashback', label: 'Platinum Tier Cashback (%)', description: 'Cashback % for Platinum tier influencers (score 76+)' },
      ],
    },
    {
      title: 'Reward Caps',
      icon: 'Shield',
      description: 'Set maximum reward limits to control platform liability.',
      settings: [
        { key: 'maxRewardPerSubmission', label: 'Max Reward Per Submission ($)', description: 'Maximum cashback reward per single submission' },
        { key: 'maxMonthlyReward', label: 'Max Monthly Reward Per User ($)', description: 'Maximum total rewards per user per month' },
        { key: 'maxCampaignBudget', label: 'Max Campaign Budget ($)', description: 'Maximum budget allowed per campaign' },
      ],
    },
    {
      title: 'Influencer Eligibility',
      icon: 'UserCheck',
      description: 'Minimum requirements for influencers to participate in campaigns.',
      settings: [
        { key: 'minFollowers', label: 'Minimum Followers', description: 'Minimum follower count to join campaigns' },
        { key: 'minScore', label: 'Minimum SC Score', description: 'Minimum SocialCents score required' },
        { key: 'minApprovalRate', label: 'Minimum Approval Rate (%)', description: 'Minimum historical approval rate required' },
      ],
    },
  ];

  const totalWeight = settings?.engagementWeight + settings?.reliabilityWeight + settings?.reachWeight;

  return (
    <div className="space-y-6">
      {totalWeight !== 100 && (
        <div className="flex items-center gap-3 p-4 bg-warning/10 border border-warning/30 rounded-xl">
          <Icon name="AlertTriangle" size={18} color="var(--color-warning)" />
          <p className="text-sm text-warning">Score weights total {totalWeight}% — they should add up to 100%</p>
        </div>
      )}
      {sections?.map((section) => (
        <div key={section?.title} className="bg-card rounded-xl border border-border p-5">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name={section?.icon} size={17} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">{section?.title}</h3>
              <p className="text-xs text-muted-foreground">{section?.description}</p>
            </div>
          </div>
          <div className="mt-4">
            {section?.settings?.map((s) => (
              <SettingRow key={s?.key} label={s?.label} description={s?.description}>
                <Input
                  type="number"
                  value={settings?.[s?.key]}
                  onChange={(e) => update(s?.key, parseFloat(e?.target?.value) || 0)}
                />
              </SettingRow>
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between p-4 bg-card rounded-xl border border-border">
        <div>
          <p className="text-sm font-medium text-foreground">Save All Settings</p>
          <p className="text-xs text-muted-foreground">Changes will take effect immediately</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <div className="flex items-center gap-1.5 text-success">
              <Icon name="CheckCircle" size={15} />
              <span className="text-sm">Saved!</span>
            </div>
          )}
          <Button variant="default" iconName="Save" iconPosition="left" onClick={handleSave}>
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlatformSettings;
